/**
 * Стили окна «Загрузить из Excel» (кредиты и лизинг): окно шире общего `Modal`
 * — под таблицу договоров; на телефоне таблица прокручивается вбок внутри окна.
 */
import styled from 'styled-components';
import { popIn } from '@shared/theme/animations';
import { fontSize, lineHeight } from '@shared/theme/typeScale';

export type ImportTone = 'info' | 'warning' | 'danger' | 'success';

// ─────────── Окно ───────────

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.z.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.background}C7;
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing(2)};
  }
`;

/** `$wide` — под таблицу договоров (просмотр); иначе — узкое окно (файл, итог). */
export const Panel = styled.div<{ $wide: boolean }>`
  width: min(${({ $wide }) => ($wide ? '1440px' : '760px')}, 100%);
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  animation: ${popIn} ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

export const Head = styled.header`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(5)} ${theme.spacing(3)}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 640px) {
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(3)}`};
  }
`;

export const HeadText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const HeadTitle = styled.h2`
  margin: 0;
  font-size: ${fontSize.xl};
  line-height: ${lineHeight.tight};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const HeadHint = styled.div`
  font-size: ${fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

export const CloseButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: -4px -6px 0 0;
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(5)}`};
  font-size: ${fontSize.sm};
  line-height: ${lineHeight.base};
  color: ${({ theme }) => theme.colors.text.primary};

  /* Не ужимать блоки под высоту окна (таблица со своей прокруткой сжалась бы
     до полоски) — прокручивается само окно. */
  & > * {
    flex-shrink: 0;
  }

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

export const Footer = styled.footer`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(5)} ${theme.spacing(4)}`};
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  }
`;

export const FooterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 0;
`;

export const Counter = styled.span`
  font-size: ${fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// ─────────── Шаг «Файл» ───────────

export const Lead = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const LinkButton = styled.button`
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.primaryHover};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
    text-decoration: none;
  }

  &:focus-visible {
    outline: none;
    border-radius: ${({ theme }) => theme.radius.xs};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

// ─────────── Сообщения ───────────

export const Notice = styled.div<{ $tone: ImportTone }>`
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme, $tone }) => theme.colors.status[$tone]};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;

  ul {
    margin: ${({ theme }) => theme.spacing(1)} 0 0;
    padding-left: ${({ theme }) => theme.spacing(5)};
  }

  li + li {
    margin-top: 2px;
  }
`;

export const NoticeTitle = styled.div`
  font-weight: 700;
`;

// ─────────── Шаг «Просмотр» ───────────

export const Summary = styled.div`
  font-size: ${fontSize.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SummaryHint = styled.div`
  margin-top: 2px;
  font-size: ${fontSize.sm};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

/** Листы файла — раскрывающийся список. */
export const SheetsBox = styled.details`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};

  & > summary {
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
    font-weight: 600;
    cursor: pointer;
    border-radius: ${({ theme }) => theme.radius.md};
  }

  & > summary:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  &[open] > summary {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => `${theme.radius.md} ${theme.radius.md} 0 0`};
  }
`;

export const SheetList = styled.ul`
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const SheetItem = styled.li<{ $skipped: boolean }>`
  color: ${({ theme, $skipped }) =>
    $skipped ? theme.colors.text.secondary : theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

export const SheetName = styled.span`
  font-weight: 700;
`;

export const SheetMeta = styled.div`
  font-size: ${fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const TableScroll = styled.div`
  overflow: auto;
  max-height: 56vh;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  -webkit-overflow-scrolling: touch;

  @media (max-width: 640px) {
    max-height: none;
  }
`;

/** Договоры из файла: строка на договор, предупреждения — строкой ниже. */
export const ReviewTable = styled.table`
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
  font-size: ${fontSize.sm};

  th,
  td {
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: ${fontSize.xs};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.backgroundElevated};
    white-space: nowrap;
    vertical-align: bottom;
  }

  th.c-check,
  td.c-check {
    width: 36px;
    text-align: center;
  }

  th.num,
  td.num {
    text-align: right;
  }

  td.num {
    white-space: nowrap;
  }

  /* Ширину текстовых колонок держит содержимое: ширина ячеек таблицы
     браузерами не ограничивается. */
  td.c-wide > *,
  td.c-status > * {
    max-width: 240px;
    overflow-wrap: anywhere;
  }

  td.c-wide > * {
    min-width: 150px;
  }

  td.nowrap {
    white-space: nowrap;
  }

  tbody tr[data-tone='danger'] td {
    background: ${({ theme }) => theme.colors.status.danger}12;
  }

  tbody tr[data-tone='muted'] td {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  tbody tr[data-split='true'] td {
    border-bottom: none;
  }

  tbody tr.notes td {
    padding-top: 0;
  }
`;

/** Ширина — по самой длинной подписи типа: подпись в списке не обрезается. */
export const CellSelect = styled.select`
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fontSize.xs};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const CellCheckbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 3px 0 0;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.primary};

  &:disabled {
    cursor: not-allowed;
  }
`;

export const Strong = styled.div`
  font-weight: 700;
`;

/** Вторая строка ячейки: дата договора, ИНН, где найден. */
export const Sub = styled.div`
  margin-top: 2px;
  font-size: ${fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Подпись над значением в ячейке: «по графику:». */
export const CellLabel = styled.span`
  display: block;
  font-size: ${fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const WarnMark = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fontSize.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.status.warning};
`;

export const StatusTag = styled.span<{ $tone: ImportTone }>`
  display: inline-block;
  padding: 1px ${({ theme }) => theme.spacing(2)};
  border: 1px solid currentColor;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: ${fontSize.xs};
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme, $tone }) => theme.colors.status[$tone]};
`;

export const ErrorText = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fontSize.xs};
  color: ${({ theme }) => theme.colors.status.danger};
`;

/** Предупреждение по договору — мелким текстом под строкой. */
export const RowNote = styled.div`
  font-size: ${fontSize.xs};
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.status.warning};
  overflow-wrap: anywhere;
`;

export const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const OptionLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;

  input {
    flex: none;
    width: 16px;
    height: 16px;
    margin: 2px 0 0;
    accent-color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }
`;

export const OptionHint = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// ─────────── Шаг «Итог» ───────────

export const ResultList = styled.ul`
  margin: ${({ theme }) => theme.spacing(1)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  overflow-wrap: anywhere;
`;

export const ResultHint = styled.p`
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
  font-size: ${fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;
